import * as React from 'react';
import { Box, Grid } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { fCurrency } from '../../../../../../../utils/formatNumber';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCoupon } from '../../../../../../../redux/slices/marketingRedux/couponRedux';
import { safeJSONParse } from '../../../../../../../utils/common';
import { toast } from 'react-toastify';
import { post } from '../../../../../../../inteceptor';

export default function Discounts({ getPaymentTypeTotalAmount, handlePriceChange, setIsModelOpen, data, getTotalPaidAmount, selectedPaymentType, currencyList }) {
  const dispatch = useDispatch();

  const { selectedLocation } = useSelector((state) => state.location || {});
  const { data: tempCouponList } = useSelector((state) => state.coupon || {});

  const [couponList, setCouponList] = useState([]);

  useEffect(() => {
    setCouponList(
      Array.isArray(tempCouponList)
        ? tempCouponList.map((value) => ({
            ...value,
            couponCustomers: safeJSONParse(value?.couponCustomers),
            couponLog: safeJSONParse(value?.couponLog),
          }))
        : []
    );
  }, [tempCouponList]);

  useEffect(() => {
    if (selectedLocation?.id && selectedPaymentType?.name) {
      dispatch(getCoupon(selectedLocation.id, selectedPaymentType.name, true, true, true));
    }
  }, [dispatch, selectedLocation?.id, selectedPaymentType?.id, selectedPaymentType?.name]);

  const totalPayable = Number(data?.paymentRecords?.totalPayable || 0);
  const totalPaid = Number(getTotalPaidAmount || 0);
  const amountLeft = Math.max(0, totalPayable - totalPaid);

  const payments = Array.isArray(data?.paymentRecords?.payments) ? data.paymentRecords.payments : [];
  const discountPayment = payments.find((p) => String(p?.name || '').toLowerCase() === 'discount');
  const activeCouponData = discountPayment?.couponData;
  const hasCouponApplied = activeCouponData && typeof activeCouponData === 'object' && !Array.isArray(activeCouponData) && Object.keys(activeCouponData).length > 0;
  const appliedCouponId = (hasCouponApplied && (activeCouponData.id ?? activeCouponData.couponId)) || null;

  const visibleCoupons = (couponList || [])
    .filter((value) => {
      const previousPurchasedCustomerIdList = Array.isArray(value?.couponLog) ? value.couponLog.map((v) => v?.customerId) : [];
      const couponCustomers = Array.isArray(value?.couponCustomers)
        ? value.couponCustomers
            .filter((cc) => {
              if (value?.canSameUserRedeemMulti === 1) return true;
              return !Boolean(cc?.redeemed) && !previousPurchasedCustomerIdList.includes(cc?.customerId);
            })
            .map((v) => v?.customerId)
        : [];
      return !value?.linkedCustomers || couponCustomers.includes(data?.customerInfo?.id);
    })
    .filter((value) => value?.currentCouponCount === null || value?.currentCouponCount > 0)
    .filter((value) => {
      // If same-user multi-redemption is explicitly disabled, check couponLog
      if (value?.canSameUserRedeemMulti === 0) {
        const alreadyRedeemed = Array.isArray(value?.couponLog) && value.couponLog.some((log) => log?.customerId === data?.customerInfo?.id);
        return !alreadyRedeemed;
      }
      return true;
    });

  const computeDiscount = (coupon) => {
    let discountAmount = 0;
    if (coupon?.type === 'Percentage') {
      discountAmount = (totalPayable * Number(coupon?.discountAmountOrPercentage || 0)) / 100;
    } else {
      discountAmount = Number(coupon?.discountAmountOrPercentage || 0);
    }
    if (totalPayable < discountAmount) discountAmount = totalPayable;
    return Math.max(0, discountAmount);
  };

  const onApplyCoupon = async (coupon) => {
    if (hasCouponApplied && (appliedCouponId == null || appliedCouponId !== coupon.id)) {
      toast.error('Only one coupon can be applied.');
      return;
    }

    const discountAmount = computeDiscount(coupon);

    if (Number(coupon?.minSaleRequired || 0) > totalPayable) {
      toast.error('This coupon requires a higher minimum sale amount.');
      return;
    }

    try {
      if (coupon?.linkedCustomers === 1) {
        const response = await post(`sendOTPForCoupon`, { customerId: data?.customerInfo?.id });
        if (response?.status) {
          const enteredOTP = window.prompt('OTP Sent. Ask it from the customer!');
          if (Number(enteredOTP) === Number(response?.otp)) {
            toast.success('OTP Verified For Coupon!');
            handlePriceChange(false, discountAmount, coupon);
            setIsModelOpen(false);
          } else {
            toast.error('Wrong OTP!');
          }
        } else {
          toast.error('Error in sending message!');
        }
      } else {
        handlePriceChange(false, discountAmount, coupon);
        setIsModelOpen(false);
      }
    } catch (e) {
      toast.error('Failed to apply coupon. Please try again.');
    }
  };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <h2>Add {selectedPaymentType?.name},</h2>
      </Box>

      <Grid container justifyContent='left' p={3}>
        <Grid item md={4}>
          <h6>Payable Amount</h6>
          <h1>{fCurrency(totalPayable)}</h1>
        </Grid>
        <Grid item md={4}>
          <h6>Total Entered Amount</h6>
          <h1>{fCurrency(totalPaid)}</h1>
        </Grid>
        <Grid item md={4}>
          <h6>Amount Left to Pay</h6>
          <h1>{fCurrency(amountLeft)}</h1>
        </Grid>
      </Grid>

      <Grid container justifyContent='left' p={3} spacing={2}>
        {visibleCoupons.map((coupon) => {
          const discountAmount = computeDiscount(coupon);
          const disabledForMinSale = Number(coupon?.minSaleRequired || 0) > totalPayable;
          const isThisApplied = hasCouponApplied && appliedCouponId != null && appliedCouponId === coupon.id;
          const disabledBySingleCouponRule = hasCouponApplied && !isThisApplied;

          return (
            <Grid item md={6} key={coupon.id}>
              <LoadingButton
                onClick={() => onApplyCoupon(coupon)}
                variant='outlined'
                size='large'
                style={{ width: '90%', marginTop: '20px' }}
                disabled={disabledForMinSale || disabledBySingleCouponRule}
              >
                {coupon?.name}
                <br />
                {isThisApplied ? 'Applied' : `Add ${fCurrency(discountAmount)}`}
              </LoadingButton>

              {disabledForMinSale && <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>Requires minimum sale: {fCurrency(coupon?.minSaleRequired)}</div>}
              {disabledBySingleCouponRule && <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>Only one coupon can be applied per payment.</div>}
            </Grid>
          );
        })}

        <Grid item xs={12}>
          <LoadingButton
            onClick={() => setIsModelOpen(false)}
            variant='contained'
            size='large'
            color='info'
            style={{
              width: '100%',
              marginTop: '50px',
              minHeight: '70px',
              fontWeight: 800,
              fontSize: '1.6em',
            }}
          >
            Confirm &amp; Close
          </LoadingButton>
        </Grid>
      </Grid>
    </>
  );
}